import { useMemo, useState } from "react";
import { ShareAltOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useLocation } from "@tanstack/react-router";
import { Button, Input, Modal, Spin, message } from "antd";
import dayjs from "dayjs";
import { type MessageKey, useLocale, useT } from "@trust/i18n";
import { listGuideFields } from "@/features/admin/api";
import { useAuthStore } from "@/lib/auth";
import { presignDownload } from "@/lib/files";
import { LevelCertificate } from "./LevelCertificate";
import { PdfViewer } from "./PdfViewer";
import { parsePdfRequest } from "./controlledPdf";
import { SimilarGuidesCard } from "./SimilarGuidesCard";
import {
  getGuide,
  listGuides,
  operateGuide,
  OP_LABELS,
  STAGE_LABELS,
  STATUS_LABELS,
  type GuideDetail,
  type GuideOperation,
} from "./api";

/**
 * 门户指南详情页(任务 P0.7)— 1:1 复刻旧站 fastj/client/src/views/inx/portal/guideline/index.vue.
 *
 * 布局:页面区 #f6f8fc;主卡 930px 白卡 + 右侧 300px 排名卡(<960px 隐藏,旧站断点 1080px 近似)。
 * 保留能力(外观融入门户样式):状态机操作按钮 + 备注 Modal(主卡底部,登录且有 allowed_operations 时显示)、
 * 分享复制链接、证书弹窗(LevelCertificate)、PDF 预览(PdfViewer,卡外下方)。
 * 文案全部走 @trust/i18n(guideline 与 guideDetail 命名空间);学科/国家沿用旧站映射。
 */

// 旧站 subjectMap:学科原始值(中/英)→ subject.* i18n key(zh 侧直接显示原始值,en 侧走字典)
const SUBJECT_KEY_MAP: Record<string, MessageKey> = {
  "社会科学-社会工作": "subject.socialWork",
  "社会科学-社会政策": "subject.socialPolicy",
  "社会科学-心理学": "subject.psychology",
  "社会科学-教育学": "subject.education",
  "社会科学-管理学": "subject.management",
  "社会科学-经济学": "subject.economics",
  "社会科学-其他": "subject.socialOther",
  "健康与医学-补充与替代医学": "subject.complementaryMedicine",
  "健康与医学-公共卫生": "subject.publicHealth",
  "健康与医学-临床医学": "subject.clinicalMedicine",
  "健康与医学-护理学": "subject.nursing",
  "健康与医学-康复医学": "subject.rehabilitationMedicine",
  "健康与医学-整合护理": "subject.integrativeCare",
  "Social Sciences - Social Work": "subject.socialWork",
  "Social Sciences - Social Policy": "subject.socialPolicy",
  "Social Sciences - Psychology": "subject.psychology",
  "Social Sciences - Education": "subject.education",
  "Social Sciences - Management": "subject.management",
  "Social Sciences - Economics": "subject.economics",
  "Social Sciences - Other": "subject.socialOther",
  "Health and Medicine - Complementary and Alternative Medicine": "subject.complementaryMedicine",
  "Health and Medicine - Public Health": "subject.publicHealth",
  "Health and Medicine - Clinical Medicine": "subject.clinicalMedicine",
  "Health and Medicine - Nursing": "subject.nursing",
  "Health and Medicine - Rehabilitation Medicine": "subject.rehabilitationMedicine",
  "Health and Medicine - Integrative Care": "subject.integrativeCare",
};

// 旧站 countryZhToEn:国家值的英译(数据值映射,非页面文案)
const COUNTRY_EN: Record<string, string> = { 中国: "China" };

/** 旧站 vscode-icons:file-type-pdf2 的近似内联图标(红色 PDF 文件) */
function PdfIcon() {
  return (
    <svg
      className="mr-[5px] inline-block h-[24px] w-[24px] align-middle"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path d="M5 1.5h9l5 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-17a2 2 0 0 1 2-2z" fill="#d32f2f" />
      <path d="M14 1.5v5h5l-5-5z" fill="#ef9a9a" />
      <text x="4.6" y="17.5" fill="#ffffff" fontSize="6.5" fontWeight="700">
        PDF
      </text>
    </svg>
  );
}

/** 灰信息卡/介绍块通用行:左 label 180px 藏蓝,右值 flex-1 #333;窄屏(<960px)改上下排 */
function InfoRow({
  label,
  value,
  fixedHeight,
}: { label: string; value: React.ReactNode; fixedHeight?: boolean }) {
  return (
    <div
      className={`${
        fixedHeight
          ? "flex h-[50px] items-center justify-start max-portal:h-auto"
          : "flex items-start"
      } max-portal:flex-col max-portal:gap-1`}
    >
      <div className="w-[180px] shrink-0 text-portal-navy max-portal:w-full">{label}:</div>
      <div className="flex-1 break-all text-[#333333] max-portal:w-full">{value}</div>
    </div>
  );
}

export function GuideDetailPage() {
  const { guideId } = useParams({ from: "/portal-layout/inx/guide/$guideId" });
  // 雪花 id 保持字符串:转 number 会丢精度(> Number.MAX_SAFE_INTEGER),详情请求 404 的根因
  const id = guideId;
  const search = useLocation({ select: (location) => location.searchStr });
  const sourceRequest = useMemo(
    () => parsePdfRequest(id, search, import.meta.env.VITE_GUIDE_LOCAL_PDF === "required"),
    [id, search],
  );
  const t = useT();
  const locale = useLocale();
  const loggedIn = useAuthStore((s) => s.loggedIn);
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();

  const [opTarget, setOpTarget] = useState<GuideOperation | null>(null);
  const [remark, setRemark] = useState("");
  const [certOpen, setCertOpen] = useState(false);

  const q = useQuery({
    queryKey: ["guide", id],
    queryFn: () => getGuide(id),
  });

  // 排名:与 RankPage 同一查询(共享缓存),在已定级/已评分 Top100 里找当前指南位次
  const rankQ = useQuery({
    queryKey: ["rank-top100"],
    queryFn: () =>
      listGuides({
        page: 1,
        page_size: 100,
        sort: "score",
        order: "desc",
        status: ["Leveled", "Scored"],
        // 与 RankPage 同一口径(共享查询缓存):只看公开评分
        show_score: true,
      }),
  });

  // P1.4 扩展字段配置(公开接口,全部启用字段,order_num 升序)
  const fieldsQ = useQuery({
    queryKey: ["guide-fields"],
    queryFn: () => listGuideFields(),
  });

  const opMutation = useMutation({
    mutationFn: (op: GuideOperation) => operateGuide(id, op, remark || undefined),
    onSuccess: (data, op) => {
      qc.setQueryData(["guide", id], data);
      msgApi.success(`${t("guideDetail.opSuccess")}:${OP_LABELS[op]}`);
      setOpTarget(null);
      setRemark("");
    },
    onError: (err: Error) => {
      msgApi.error(err.message);
    },
  });

  const rank = useMemo(() => {
    const items = rankQ.data?.items;
    if (!items) return null;
    const idx = items.findIndex((it) => it.id === id);
    return idx >= 0 ? idx + 1 : null;
  }, [rankQ.data, id]);

  if (q.isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-160px)] w-full items-center justify-center bg-portal-bg-detail">
        <Spin />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="flex min-h-[calc(100vh-160px)] w-full flex-col items-center justify-center bg-portal-bg-detail">
        <img src="/portal/nodata.png" alt="no data" className="w-[200px]" />
        <div className="mt-[10px] text-[#666666]">
          {t("guideDetail.loadFailed")}:{q.error?.message}
        </div>
      </div>
    );
  }

  const g: GuideDetail = q.data;

  // P1.4 扩展字段:按配置顺序渲染 extra_fields 的非空值
  const extraRows = (fieldsQ.data ?? [])
    .map((field) => ({ field, value: g.extra_fields?.[field.field] }))
    .filter((r) => r.value != null && String(r.value).trim() !== "");

  // 双语:英文标题优先用 DB 的 title_en 字段(旧站是硬编码 zh→en 映射表)
  const titleText = locale === "en" ? g.title_en || g.title : g.title;

  function subjectText(subject: string | null): string {
    if (!subject) return "";
    if (locale === "zh") return subject;
    const key = SUBJECT_KEY_MAP[subject];
    return key ? t(key) : subject;
  }

  function countryText(country: string | null): string {
    if (!country) return "";
    return locale === "en" ? (COUNTRY_EN[country] ?? country) : country;
  }

  async function sharePage() {
    const url = `${window.location.origin}/inx/guide/${g.id}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        msgApi.success(t("guideDetail.shareCopied"));
      } else {
        // 浏览器不支持 Clipboard API → 降级弹窗让用户手动复制
        window.prompt(t("guideDetail.copyPrompt"), url);
      }
    } catch {
      window.prompt(t("guideDetail.copyPrompt"), url);
    }
  }

  // 文档下载:完整 URL 直接打开(旧数据);object_key 走 /files/presign-download 换签名 URL
  const pdfIsUrl = !!g.pdf_url && /^https?:\/\//i.test(g.pdf_url);
  async function downloadPdf() {
    if (!g.pdf_url) return;
    try {
      const resp = await presignDownload(g.pdf_url, `${g.title}.pdf`);
      window.open(resp.download_url, "_blank", "noopener");
    } catch (err) {
      msgApi.error((err as Error).message);
    }
  }

  // 操作区:仅登录且状态机给出可执行操作时显示
  const showOps = loggedIn && g.allowed_operations.length > 0;
  // 证书:有等级即可查看(对齐旧站:Scored 带等级就展示;show_score=false 时后端已隐藏 level)
  const canViewCert = !!g.level;

  return (
    <div className="min-h-[calc(100vh-160px)] w-full bg-portal-bg-detail">
      {msgCtx}
      <div className="flex w-full justify-center">
        {/* ===== 主卡 ===== */}
        <div className="mt-[20px] min-h-[700px] w-[930px] rounded-[20px] bg-white p-[20px] max-portal:w-full">
          {/* 标题行:版本 tag + 日期 tag + 标题(旧站无条件保留 20px 间隔;移动端允许换行,tag 不压缩)*/}
          <div className="flex items-center justify-start text-[16px] font-semibold max-portal:flex-wrap max-portal:gap-x-2 max-portal:gap-y-2">
            {g.edition && (
              <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full bg-tag-edition px-[10px] font-normal text-[12px] text-white">
                {g.edition}
              </span>
            )}
            <div className="w-[20px] shrink-0 max-portal:hidden" />
            {g.end_date && (
              <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full bg-[#909399] px-[10px] font-normal text-[12px] text-white">
                {g.end_date}
              </span>
            )}
            <div className="w-[20px] shrink-0 max-portal:hidden" />
            <span className="text-[20px] font-semibold text-[#333333]">{titleText}</span>
          </div>
          {/* AI 评审 Tab 已移除(AI 页面整体下线,待重做) */}
          <>
            {/* 灰信息卡:所属学科 / 发布杂志 / 制订机构 */}
            <div className="mt-[20px] rounded-[10px] bg-portal-gray-card p-[20px]">
              <InfoRow
                fixedHeight
                label={t("guideline.subject")}
                value={subjectText(g.subject_classification)}
              />
              <InfoRow fixedHeight label={t("guideline.journal")} value={g.journal} />
              <InfoRow fixedHeight label={t("guideline.organization")} value={g.register} />
            </div>
            {/* 介绍 */}
            <div className="mt-5 px-[20px]">
              <div className="mb-[20px] text-[18px] font-medium">{t("guideline.introduction")}</div>
              <div className="space-y-[15px]">
                <InfoRow label={t("guideline.regNo")} value={g.reg_no} />
                <InfoRow
                  label={t("guideline.regTime")}
                  value={dayjs(g.created_at).format("YYYY-MM-DD")}
                />
                <InfoRow label={t("guideline.country")} value={countryText(g.country)} />
                <InfoRow label={t("guideline.startDate")} value={g.start_date} />
                <InfoRow label={t("guideline.endDate")} value={g.end_date} />
                <InfoRow label={t("guideline.fundSource")} value={g.source_of_funds} />
              </div>
            </div>
            {/* P1.4 扩展字段(按 guide_fields 配置渲染 extra_fields 非空值)*/}
            {extraRows.length > 0 && (
              <div className="mt-5 px-[20px]">
                <div className="mb-[20px] text-[18px] font-medium">
                  {t("guideFields.sectionTitle")}
                </div>
                <div className="space-y-[15px]">
                  {extraRows.map(({ field, value }) => (
                    <InfoRow
                      key={field.id}
                      label={locale === "en" ? field.label_en || field.label : field.label}
                      value={String(value)}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* 原文链接 + 文档下载(窄屏 label 上下排,同 InfoRow) */}{" "}
            <div className="mt-5 px-[20px]">
              <div className="flex items-start max-portal:flex-col max-portal:gap-1">
                <div className="w-[180px] shrink-0 text-portal-navy max-portal:w-full">
                  {t("guideline.originalLink")}:
                </div>
                <div className="flex-1">
                  {g.origin_link ? (
                    <a
                      href={g.origin_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[16px] text-portal-primary hover:underline"
                    >
                      {titleText}
                    </a>
                  ) : (
                    <span className="text-[#666666]">-</span>
                  )}
                </div>
              </div>
              {g.pdf_url && sourceRequest.kind === "legacy" && (
                <div className="mt-[15px] flex items-start max-portal:flex-col max-portal:gap-1">
                  <div className="w-[180px] shrink-0 text-portal-navy max-portal:w-full">
                    {t("guideline.docDownload")}:
                  </div>
                  <div className="flex-1">
                    {pdfIsUrl ? (
                      <a
                        href={g.pdf_url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[16px] text-portal-primary hover:underline"
                      >
                        <PdfIcon />
                        {g.title}.pdf
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void downloadPdf()}
                        className="cursor-pointer border-0 bg-transparent p-0 text-[16px] text-portal-primary hover:underline"
                      >
                        <PdfIcon />
                        {g.title}.pdf
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
          {/* 操作区(主卡底部):状态机操作按钮(登录且有权限)+ 分享 */}
          <div className="mx-[20px] mt-[30px] flex flex-wrap items-center gap-[10px] border-[#f0f0f0] border-t pt-[20px]">
            {showOps &&
              g.allowed_operations.map((op) => (
                <Button
                  key={op}
                  type={op === "approve" || op === "final_approve" ? "primary" : "default"}
                  danger={op === "reject" || op === "request_rectify"}
                  onClick={() => setOpTarget(op)}
                >
                  {OP_LABELS[op]}
                </Button>
              ))}
            <Button icon={<ShareAltOutlined />} onClick={sharePage} className="ml-auto">
              {t("guideDetail.share")}
            </Button>
          </div>
          <div className="h-[50px]" />
        </div>

        {/* ===== 右栏(仅桌面,旧站 <1080px 隐藏):排名卡 + P3.6 相似指南卡 ===== */}
        <div className="ml-[15px] w-[300px] shrink-0 max-portal:hidden">
          <div className="mt-[20px] h-[200px] rounded-[20px] bg-white">
            <div className="h-[100px] p-[20px]">
              <div className="flex items-center justify-between text-[20px] font-semibold text-[#333333]">
                <div>{t("guideline.ranking")}</div>
                <Link
                  to="/inx/rank"
                  className="font-normal text-[14px] text-[#606266] no-underline hover:text-portal-primary"
                >
                  {t("guideline.more")}
                </Link>
              </div>
              <div className="mt-[30px] flex items-center justify-between">
                <div className="w-[200px] text-[#333333]">{t("guideline.rank")}:</div>
                <div className="break-all leading-[20px] text-[#666666]">
                  {rank ?? t("guideline.none")}
                </div>
              </div>
              <div className="mt-[10px] flex items-center justify-between">
                <div className="w-[200px] text-[#333333]">{t("guideline.level")}:</div>
                <div className="break-all leading-[20px] text-[#666666]">
                  {g.level ?? t("guideline.none")}
                </div>
              </div>
              <div className="mt-[10px] flex items-center justify-between">
                <div className="w-[200px] text-[#333333]">{t("guideline.score")}:</div>
                <div className="break-all leading-[20px]">
                  {g.score != null ? (
                    <span className="text-portal-gold">{g.score}</span>
                  ) : (
                    <span className="text-[#666666]">{t("guideline.none")}</span>
                  )}
                </div>
              </div>
              <div className="mt-[10px] flex items-center justify-between">
                <div className="w-[200px] text-[#333333]">{t("guideline.certificate")}:</div>
                <div className="break-all leading-[20px] text-[#666666]">
                  {canViewCert ? (
                    <button
                      type="button"
                      onClick={() => setCertOpen(true)}
                      className="cursor-pointer border-0 bg-transparent p-0 text-portal-primary hover:underline"
                    >
                      {t("guideline.view")}
                    </button>
                  ) : (
                    t("guideline.none")
                  )}
                </div>
              </div>
            </div>
          </div>
          <SimilarGuidesCard guideId={id} />
        </div>
      </div>

      {/* PDF 预览(卡外下方,与主卡+排名卡同宽)*/}
      {(g.pdf_url || g.origin_link || sourceRequest.kind !== "legacy") && (
        <div className="flex w-full justify-center pb-[40px]">
          <div className="mt-[15px] w-[1245px] max-w-full">
            <PdfViewer
              pdfUrl={g.pdf_url}
              originLink={g.origin_link}
              sourceRequest={sourceRequest}
            />
          </div>
        </div>
      )}

      {/* 证书弹窗(Canvas 复刻旧站)*/}
      <LevelCertificate
        open={certOpen}
        onClose={() => setCertOpen(false)}
        data={{
          reg_no: g.reg_no,
          title: g.title,
          register: g.register,
          level: g.level,
          created_at: g.created_at,
        }}
      />

      {/* 状态机操作确认 + 备注 Modal(保留)*/}
      <Modal
        title={`${t("guideDetail.confirmOp")}:${opTarget ? OP_LABELS[opTarget] : ""}`}
        open={opTarget !== null}
        okButtonProps={{ loading: opMutation.isPending }}
        onOk={() => opTarget && opMutation.mutate(opTarget)}
        onCancel={() => {
          setOpTarget(null);
          setRemark("");
        }}
      >
        <p className="text-[#666666]">
          {t("guideDetail.currentStatus")} {STAGE_LABELS[g.stage]} / {STATUS_LABELS[g.status]}
        </p>
        <Input.TextArea
          rows={3}
          placeholder={t("guideDetail.remarkPlaceholder")}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </Modal>
    </div>
  );
}
